import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useSidebar } from '../../hooks/useSidebar';

interface RightPanelContextType {
  isOpen: boolean;
  toggle: (isOpen?: boolean) => void;
  open: () => void;
  close: () => void;
  content: ReactNode | null;
  setContent: (content: ReactNode | null) => void;
  /** Width in pixels that the right panel occupies. AppLayout uses this for the push margin. */
  panelWidth: number;
  setPanelWidth: (width: number) => void;
}

const RightPanelContext = createContext<RightPanelContextType | undefined>(undefined);

interface RightPanelProviderProps {
  children: ReactNode;
}

export const RightPanelProvider: React.FC<RightPanelProviderProps> = ({ children }) => {
  const [content, setContent] = React.useState<ReactNode | null>(null);
  const [panelWidth, setPanelWidth] = React.useState<number>(320);

  const { isOpen, toggle, open, close } = useSidebar({
    sidebarKey: 'right-panel',
    defaultOpen: false,
    autoInitialize: false
  });

  const value = useMemo<RightPanelContextType>(
    () => ({
      isOpen,
      toggle,
      open,
      close,
      content,
      setContent,
      panelWidth,
      setPanelWidth
    }),
    [isOpen, toggle, open, close, content, setContent, panelWidth, setPanelWidth]
  );

  return <RightPanelContext.Provider value={value}>{children}</RightPanelContext.Provider>;
};

export const useRightPanel = (): RightPanelContextType => {
  const context = useContext(RightPanelContext);
  if (context === undefined) {
    throw new Error('useRightPanel must be used within a RightPanelProvider');
  }
  return context;
};
